import { RouteLocationNormalized, RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router';
import { APP_THEME_SABLE, AppThemeModel } from '../../../../common/src';
import { getJoinFlowDefinition, joinFlowPageForPath } from './join-flow-registry';

export function isJoinFlowRoute(path: string): boolean {
  const lowerPath = path.toLowerCase();
  return lowerPath === '/join' || lowerPath.startsWith('/join/');
}

export function getJoinFlowRedirect(theme: AppThemeModel, route: RouteLocationNormalizedLoaded): RouteLocationRaw | undefined {
  if (!isJoinFlowRoute(route.path)) {
    return undefined;
  }

  // `/join/details/` and `/join/Details` follow the same theme rules as `/join/details`.
  const page = joinFlowPageForPath(route.path);
  if (!page) {
    return undefined;
  }

  const pages = getJoinFlowDefinition(theme).pages;

  // sable theme's /join is only a layout shell, so hitting it lands on an empty page. Send the flow to
  // its first step instead. Guarded on `pages.center` so a theme without one cannot ping-pong
  // against the unsupported-page redirect below.
  if (page === 'root' && theme === APP_THEME_SABLE && pages.center) {
    return { path: '/join/center', query: route.query };
  }

  // A page is supported only when the active theme explicitly registers its component.
  // No `replace`: a redirect's own replace overrides the outer navigation (vue-router assigns it
  // over the inherited value), which would rewrite the entry the user came from, not /join.
  return pages[page] ? undefined : { path: '/join', query: route.query };
}

/** Applies the active theme's access rule to the current navigation and its persistent route record. */
export function applyJoinFlowRouteMeta(theme: AppThemeModel, route: RouteLocationNormalized): void {
  const page = joinFlowPageForPath(route.path);
  if (!page) {
    return;
  }

  const access = getJoinFlowDefinition(theme).pages[page]?.access;
  const matchedRecord = route.matched[route.matched.length - 1];
  if (access) {
    route.meta.access = access;
    if (matchedRecord) {
      matchedRecord.meta.access = access;
    }
    return;
  }

  // Remove access left by a previous theme because route records persist between navigations.
  delete route.meta.access;
  if (matchedRecord) {
    delete matchedRecord.meta.access;
  }
}
